import type { EChartsOption } from "echarts";
import { EChartRender } from "../../../implementations/chart/EChartRender";
import { EstadisticaLoteResponse } from "../../../../modulos/satelite/response/EstadisticaLoteResponse";
import { formatDiaMesAbr, toDateOnly } from "../../../../utils/fechaUtils";

/** Gris de la banda q1–q3 (línea base del relleno `rgba(148, 163, 184, …)`),
 *  reutilizado para el punto de "Máximo"/"Mínimo" en el tooltip. */
const COLOR_BANDA = "#94a3b8";

/** Chart de tendencia del índice activo para el lote seleccionado: línea de
 *  `average` (color = último de `rangoColores` del índice) + banda gris
 *  `q1`–`q3` de fondo (rango intercuartil, no el mínimo/máximo real).
 *
 *  Tooltip default de ECharts (`trigger: "axis"`, sin formatter propio —
 *  mismo criterio que los charts de Clima), no uno armado a mano: "Máximo"
 *  y "Mínimo" llevan su valor real (q3/q1) como serie invisible propia, y
 *  solo la serie auxiliar del relleno (la resta q3-q1, sin significado por
 *  sí sola) se oculta del tooltip con `tooltip: { show: false }`. */
export class SateliteEstadisticaChartRender extends EChartRender {
  /**
   * @param _estadisticas Estadísticas del lote, ya filtradas por índice y
   *   rango de fechas.
   * @param _color Color de la línea de promedio (último de `rangoColores`
   *   del índice activo).
   */
  constructor(
    private readonly _estadisticas: EstadisticaLoteResponse[],
    private readonly _color: string,
  ) {
    super();
  }

  protected getOption(): EChartsOption {
    const ordenadas = [...this._estadisticas].sort((a, b) =>
      a.fecha.localeCompare(b.fecha),
    );
    // Fecha con año (título del tooltip = valor del eje en el punto activo).
    const fechas = ordenadas.map(
      (e) => `${toDateOnly(e.fecha).slice(0, 4)}, ${formatDiaMesAbr(e.fecha)}`,
    );
    const q1 = ordenadas.map((e) => e.q1);
    const q3 = ordenadas.map((e) => e.q3);
    const rango = ordenadas.map((e) => e.q3 - e.q1);
    const average = ordenadas.map((e) => e.average);

    return {
      grid: { left: 36, right: 12, top: 16, bottom: 24 },
      xAxis: { type: "category", data: fechas, boundaryGap: false },
      yAxis: { type: "value" },
      tooltip: {
        trigger: "axis",
        valueFormatter: (value) =>
          typeof value === "number" ? value.toFixed(2) : String(value),
      },
      series: [
        {
          name: "Máximo",
          type: "line",
          data: q3,
          smooth: true,
          color: COLOR_BANDA,
          symbol: "none",
          lineStyle: { opacity: 0 },
        },
        {
          name: "Promedio",
          type: "line",
          data: average,
          smooth: true,
          color: this._color,
          emphasis: { focus: "series" },
        },
        {
          name: "Mínimo",
          type: "line",
          data: q1,
          smooth: true,
          stack: "banda",
          color: COLOR_BANDA,
          symbol: "none",
          lineStyle: { opacity: 0 },
        },
        {
          name: "Rango (q1–q3)",
          type: "line",
          data: rango,
          smooth: true,
          stack: "banda",
          symbol: "none",
          lineStyle: { opacity: 0 },
          areaStyle: { color: "rgba(148, 163, 184, 0.35)" },
          tooltip: { show: false },
        },
      ],
    };
  }
}
